import { Asset } from "@iventis/domain-model/model/asset";
import { AssetCategory } from "@iventis/domain-model/model/assetCategory";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { BackgroundCategoryCard, BackgroundCategorySection, BackgroundSelectorPayload, BackgroundSubSection, DefaultMapOptions } from "./background-selector-types";

export type { BackgroundCategoryCard, BackgroundCategorySection, BackgroundSelectorPayload, BackgroundSubSection, DefaultMapOptions };

/** Special tag name that means "no background". Assets with this tag show a prohibition icon instead of a thumbnail. */
export const NONE_TAG = "None";

/**
 * Determines whether the current background selector payload differs from the saved defaults.
 * Used to enable or disable the save button.
 *
 * @param payload - The current state of the background selector.
 * @param savedBackgroundId - The asset ID of the previously saved background map.
 * @param defaultOptions - The map option values that were active when the selector was opened.
 * @returns `true` if any value has changed from the saved/default state.
 */
export function hasBackgroundSelectorPayloadChanged(payload: BackgroundSelectorPayload, savedBackgroundId: string, defaultOptions: DefaultMapOptions): boolean {
    return (
        payload.backgroundMap?.id !== savedBackgroundId ||
        payload.globeEnabled !== defaultOptions.globeEnabled ||
        payload.terrainEnabled !== defaultOptions.terrainEnabled ||
        payload.buildings3dEnabled !== defaultOptions.buildings3dEnabled ||
        payload.streetNamesEnabled !== defaultOptions.streetNamesEnabled
    );
}

/**
 * Filters a list of background assets to only those compatible with the current sitemap type.
 * When a 3D sitemap is selected, only assets tagged as 3D-compatible are included.
 *
 * @param backgrounds - The full list of available background assets.
 * @param is3dSitemapSelected - Whether the currently selected sitemap contains a 3D version.
 * @returns The filtered array of compatible background assets.
 */
export function filterBackgroundsForSitemap(backgrounds: Asset[], is3dSitemapSelected: boolean): Asset[] {
    return backgrounds.filter((background) => !is3dSitemapSelected || background.metaData?.["3dSitemapCompatible"]);
}

/** Sorts assets with recommended (metaData.default === "true") first, then alphabetically by name. */
function sortByName(assets: Asset[]): Asset[] {
    return [...assets].sort((a, b) => {
        const aDefault = a.metaData?.default === "true" ? 0 : 1;
        const bDefault = b.metaData?.default === "true" ? 0 : 1;
        if (aDefault !== bDefault) return aDefault - bDefault;
        return a.name.localeCompare(b.name);
    });
}

/** Sorts tag hierarchy nodes by their `order` property (nullish values sort last). */
function sortByOrder(tags: Hierarchy<AssetCategory>[]): Hierarchy<AssetCategory>[] {
    return [...tags].sort((a, b) => (a.item.order ?? Number.MAX_SAFE_INTEGER) - (b.item.order ?? Number.MAX_SAFE_INTEGER));
}

/** Returns assets whose tags include `tagName`, sorted alphabetically. */
function assetsWithTag(assets: Asset[], tagName: string, excludeIds?: Set<string>): Asset[] {
    const filtered = assets.filter((bg) => bg.tags?.includes(tagName) && (!excludeIds || !excludeIds.has(bg.id)));
    return sortByName(filtered);
}

/** Marks all assets as assigned by adding their IDs to the set. */
function markAssigned(assets: Asset[], assigned: Set<string>): void {
    assets.forEach((bg) => assigned.add(bg.id));
}

/**
 * Builds a section for a leaf tag (one with no children).
 * All matching backgrounds go directly under a single un-labelled sub-section.
 */
function buildLeafSection(tag: AssetCategory, filteredBackgrounds: Asset[], assigned: Set<string>): BackgroundCategorySection | null {
    const backgrounds = assetsWithTag(filteredBackgrounds, tag.name);
    markAssigned(backgrounds, assigned);

    if (backgrounds.length === 0) return null;
    return { label: tag.name, subSections: [{ label: null, backgrounds }] };
}

/**
 * Builds a section for a parent tag that has child sub-categories.
 * Each child tag becomes a labelled sub-section.
 * Backgrounds tagged with the parent but no child are placed in an un-labelled sub-section at the top.
 */
function buildParentSection(tag: AssetCategory, children: Hierarchy<AssetCategory>[], filteredBackgrounds: Asset[], assigned: Set<string>): BackgroundCategorySection | null {
    // Build a sub-section for each child tag, respecting their order property
    const childSubSections: BackgroundSubSection[] = sortByOrder(children)
        .map(({ item: childTag }) => {
            const backgrounds = assetsWithTag(filteredBackgrounds, childTag.name);
            markAssigned(backgrounds, assigned);
            if (backgrounds.length === 0) return null;
            // Strip the parent category name prefix from the child label (e.g. "Map Global" → "Global" under parent "Map")
            const displayLabel = childTag.name.startsWith(tag.name) ? childTag.name.slice(tag.name.length).trim() || childTag.name : childTag.name;
            return { label: displayLabel, backgrounds };
        })
        .filter((s) => s != null);

    // Backgrounds tagged with the parent but not any child
    const parentOnly = assetsWithTag(filteredBackgrounds, tag.name, assigned);
    markAssigned(parentOnly, assigned);
    if (parentOnly.length > 0) {
        childSubSections.unshift({ label: null, backgrounds: parentOnly });
    }

    if (childSubSections.length === 0) return null;
    return { label: tag.name, subSections: childSubSections };
}

/**
 * Groups a list of background assets into hierarchical category sections based on tag data.
 * Top-level tags form sections; child tags form sub-sections within them.
 * Backgrounds not matching any tag are collected under an "Other" section.
 * If tag data has not yet loaded, all backgrounds are returned in a single un-labelled section.
 *
 * @param filteredBackgrounds - The list of backgrounds already filtered for the current sitemap type.
 * @param mapBackgroundTags - The tag hierarchy returned by the API, or undefined if not yet loaded.
 * @returns An array of category sections, each containing one or more sub-sections of backgrounds.
 */
export function buildCategorySections(filteredBackgrounds: Asset[], mapBackgroundTags: Hierarchy<AssetCategory>[] | undefined): BackgroundCategorySection[] {
    if (!mapBackgroundTags) {
        return [{ label: null, subSections: [{ label: null, backgrounds: filteredBackgrounds }] }];
    }

    const assigned = new Set<string>();

    const sections = sortByOrder(mapBackgroundTags)
        .map(({ item: tag, children }) =>
            children.length === 0 ? buildLeafSection(tag, filteredBackgrounds, assigned) : buildParentSection(tag, children, filteredBackgrounds, assigned)
        )
        .filter((s) => s != null);

    // Anything not matched by any tag goes into "Other"
    const uncategorised = sortByName(filteredBackgrounds.filter((bg) => !assigned.has(bg.id)));
    if (uncategorised.length > 0) {
        sections.push({ label: "Other", subSections: [{ label: null, backgrounds: uncategorised }] });
    }

    return sections;
}

/**
 * Determines which top-level category tag the currently selected background belongs to.
 *
 * @param selectedMapBackgroundId - The ID of the currently selected background asset.
 * @param mapBackgrounds - The full list of available background assets.
 * @param mapBackgroundTags - The tag hierarchy returned by the API.
 * @returns The name of the matching category tag, or `null` if none is found.
 */
export function getSelectedBackgroundCategory(selectedMapBackgroundId: string, mapBackgrounds: Asset[], mapBackgroundTags: Hierarchy<AssetCategory>[]): string | null {
    const bg = mapBackgrounds.find((b) => b.id === selectedMapBackgroundId);
    if (!bg?.tags) return null;
    const tagNames = mapBackgroundTags.map((t) => t.item.name);
    return tagNames.find((tag) => bg.tags.includes(tag)) ?? null;
}

/**
 * Builds an ordered list of category cards to display in the quick-picker strip.
 * Each card represents one top-level category and carries the most relevant representative asset:
 * the selected asset (if it belongs to that category), then the default asset, then the first asset.
 * Categories with no assets are omitted.
 *
 * @param mapBackgrounds - The full list of available background assets.
 * @param mapBackgroundTags - The tag hierarchy returned by the API.
 * @param selectedMapBackgroundId - The ID of the currently selected background asset.
 * @returns An ordered array of category cards.
 */
export function buildCategoryCards(mapBackgrounds: Asset[], mapBackgroundTags: Hierarchy<AssetCategory>[], selectedMapBackgroundId: string): BackgroundCategoryCard[] {
    return sortByOrder(mapBackgroundTags)
        .map(({ item: tag }) => {
            const assetsInCategory = mapBackgrounds.filter((a) => a.tags?.includes(tag.name)).sort((a, b) => a.name.localeCompare(b.name));
            if (assetsInCategory.length === 0) return null;
            const selected = assetsInCategory.find((a) => a.id === selectedMapBackgroundId);
            const defaultAsset = assetsInCategory.find((a) => a.metaData?.default === "true");
            const representative = selected ?? defaultAsset ?? assetsInCategory[0];
            return { category: tag.name, asset: representative } as BackgroundCategoryCard;
        })
        .filter((card) => card != null);
}
